import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';

const TemplateForm = ({ 
  onSave, 
  initialData = null,
  onCancel,
  isEditing = false,
  saveButtonText = null,
  cancelButtonText = 'Cancel'
}) => {
  const [formData, setFormData] = useState({
    name: '',
    content: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [touched, setTouched] = useState({
    name: false,
    content: false
  });

  // Initialize form with initialData when editing
  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        content: initialData.message || ''
      });
    }
  }, [initialData]);

  const validateField = useCallback((fieldName, value) => {
    const newErrors = { ...errors };
    
    if (fieldName === 'name' && !value.trim()) {
      newErrors.name = 'Template name is required';
    } else if (fieldName === 'name') {
      delete newErrors.name;
    }
    
    if (fieldName === 'content' && !value.trim()) {
      newErrors.content = 'Message content is required';
    } else if (fieldName === 'content') {
      delete newErrors.content;
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [errors]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (touched[name]) {
      validateField(name, value);
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched(prev => ({
      ...prev,
      [name]: true
    }));
    validateField(name, value);
  };

  const validateForm = useCallback(() => {
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = 'Template name is required';
    if (!formData.content.trim()) newErrors.content = 'Message content is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setTouched({
      name: true,
      content: true
    });
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    try {
      await onSave({ 
        name: formData.name.trim(), 
        message: formData.content.trim() 
      });
      
      if (!isEditing) {
        // Reset form only when creating new templates
        setFormData({
          name: '',
          content: ''
        });
        setTouched({
          name: false,
          content: false
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    }
    setFormData({
      name: '',
      content: ''
    });
    setErrors({});
    setTouched({
      name: false,
      content: false
    });
  };

  const getButtonText = () => {
    if (saveButtonText) return saveButtonText;
    return isEditing ? 'Update Template' : 'Save Template';
  };

  return (
    <form onSubmit={handleSubmit} className="template-form" noValidate>
      <h3 className="form-title">
        {isEditing ? 'Edit Template' : 'Create New Template'}
      </h3>
      
      <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
        <label htmlFor="template-name">Template Name *</label>
        <input
          id="template-name"
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="e.g. Welcome Message"
          disabled={isSubmitting}
          required
          aria-invalid={!!errors.name}
          aria-describedby={errors.name ? "name-error" : undefined}
        />
        {errors.name && (
          <span id="name-error" className="error-message">
            {errors.name}
          </span>
        )}
      </div>
      
      <div className={`form-group ${errors.content ? 'has-error' : ''}`}>
        <label htmlFor="template-content">Message Content *</label>
        <textarea
          id="template-content"
          name="content"
          value={formData.content}
          onChange={handleChange}
          onBlur={handleBlur}
          rows={6}
          placeholder="Enter your message template..."
          disabled={isSubmitting}
          required
          aria-invalid={!!errors.content}
          aria-describedby={errors.content ? "content-error" : undefined}
        />
        {errors.content && (
          <span id="content-error" className="error-message">
            {errors.content}
          </span>
        )}
      </div>
      
      <div className="form-actions">
        {isEditing && (
          <button 
            type="button" 
            onClick={handleCancel}
            className="cancel-btn"
            disabled={isSubmitting}
          >
            {cancelButtonText}
          </button>
        )}
        <button 
          type="submit" 
          className="save-btn"
          disabled={isSubmitting || Object.keys(errors).length > 0}
        >
          {isSubmitting ? (
            <>
              <span className="spinner" aria-hidden="true"></span>
              {isEditing ? 'Updating...' : 'Saving...'}
            </>
          ) : (
            getButtonText()
          )}
        </button>
      </div>
    </form>
  );
};

TemplateForm.propTypes = {
  onSave: PropTypes.func.isRequired,
  initialData: PropTypes.shape({
    name: PropTypes.string,
    message: PropTypes.string
  }),
  onCancel: PropTypes.func,
  isEditing: PropTypes.bool,
  saveButtonText: PropTypes.string,
  cancelButtonText: PropTypes.string
};

TemplateForm.defaultProps = {
  isEditing: false,
  cancelButtonText: 'Cancel'
};

export default TemplateForm;